import { useLocalization } from "@fluent/react";
import { ContentCard, Row } from "@narmi/design_system";
import { BackButton } from "@lending/components/BackButton/BackButton";
import { PageContent } from "@lending/components/PageContent";
import { PageHeader } from "@lending/components/PageHeader";
import { PageLayout } from "@lending/components/PageLayout/PageLayout";
import { useDeleteApplicationApplicantDetailsQuery } from "@lending/hooks/queries/ApplicationApplicantDetails/useDeleteApplicationApplicantDetailsQuery";
import { useGetCoApplicantQuery } from "@lending/hooks/queries/useGetCoApplicantQuery";
import { useFlowNavigation } from "@lending/navigation";
import styles from "./CoApplicantAsk.module.css";
import { useStore } from "../../../../contexts/StoreContext";

function CoApplicantAsk() {
  const { updateData } = useStore();
  const { goNext, goBack } = useFlowNavigation();
  const { l10n } = useLocalization();
  const { coApplicant, isLoading } = useGetCoApplicantQuery();
  const { mutateAsync: deleteApplicant, isPending: isDeleting } =
    useDeleteApplicationApplicantDetailsQuery();

  function onClickYes() {
    updateData({ hasCoApplicant: true });
    goNext({ hasCoApplicant: true });
  }

  async function onClickNo() {
    if (coApplicant) {
      try {
        await deleteApplicant({
          params: {
            path: {
              uuid: coApplicant.id,
            },
          },
        });
      } catch {
        return;
      }
    }

    updateData({ hasCoApplicant: false });
    goNext({ hasCoApplicant: false });
  }

  return (
    <PageLayout isLoading={isLoading}>
      <PageContent>
        <PageHeader
          heading={l10n.getString(
            "co-applicants-ask-heading",
            null,
            "Would you like to add a co-borrower?"
          )}
          subheading={l10n.getString(
            "co-applicants-ask-subheading",
            null,
            "We'll consider your combined credit and income, which may get you a better rate and larger loan amount."
          )}
        />
        <div className={styles.optionCards}>
          <ContentCard kind="button" onClick={onClickYes} radiusSize="m" disabled={isDeleting}>
            <div className={styles.cardIcon}>
              <span className="narmi-icon-users fontSize--l" />
            </div>
            <div className="fontSize--m fontWeight--bold margin--top--xs">
              {l10n.getString("co-applicants-ask-yes-button-title", null, "Yes, add a co-borrower")}
            </div>
            <div className="margin--top--xs fontSize--xs fontColor--secondary">
              {l10n.getString(
                "co-applicants-ask-yes-button-description",
                null,
                "May help you qualify for a larger loan that has a lower rate"
              )}
            </div>
          </ContentCard>
          <ContentCard kind="button" onClick={onClickNo} radiusSize="m" disabled={isDeleting}>
            <div className={styles.cardIcon}>
              <span className="narmi-icon-user fontSize--l" />
            </div>
            <div className="fontSize--m fontWeight--bold margin--top--xs">
              {l10n.getString("co-applicants-ask-no-button-title", null, "No, it’s just me")}
            </div>
            <div className="margin--top--xs fontSize--xs fontColor--secondary">
              {l10n.getString(
                "co-applicants-ask-no-button-description",
                null,
                "Easily check your offer based on your credit profile"
              )}
            </div>
          </ContentCard>
        </div>
        <div className={styles.backButtonRow}>
          <Row alignItems="center" justifyContent="space-between">
            <BackButton onClick={goBack} />
          </Row>
        </div>
      </PageContent>
    </PageLayout>
  );
}

export { CoApplicantAsk };
